import { useCallback } from 'react';

import { BRAND_VOICE_ASSET, BRAND_VOICE_ASSET_FIELD } from 'src/data/brandVoice';
import { CHANNEL, isBlogUrlsChannel, isMetaChannel } from 'src/data/channel';
import { CUSTOM_MODEL_STATUS } from 'src/data/customModel';
import { getToolChannel } from 'src/data/generationTool';
import { getIsEmailChannel } from 'src/data/performance';
import * as events from 'src/lib/events';
import { injectFacebookSDK } from 'src/lib/facebook';
import { ROUTES } from 'src/routes';
import {
  PAGES,
  useBrandVoices,
  useConnectToChannel,
  useEmailConnectedAccounts,
  useErrorHandler,
  useHistoryPushOrInfinityOpen,
  useTrackingEvent,
} from 'src/services';
import {
  useBrandVoicesActions,
  useCustomModelsActions,
  useNotificationActions,
  useProjectActions,
} from 'src/store';
import { useAccountExtractionLoaderPopup } from 'src/views/AccountExtractionLoaderPopup';
import { useBlogCustomModelPopup } from 'src/views/BlogCustomModelPopup';
import { useConnectToChannelPopup } from 'src/views/ConnectToChannelPopup';
import { useCustomModelPopup } from 'src/views/CustomModelPopup';
import { useCustomModelSuccessPopup } from 'src/views/CustomModelSuccessPopup';

export const useCreateCustomModel = (generationTool, onSelect) => {
  const { brandVoices } = useBrandVoices();
  const brandVoicesActions = useBrandVoicesActions();
  const openCustomModelPopup = useCustomModelPopup(generationTool);
  const openCustomModelSuccessPopup = useCustomModelSuccessPopup();
  const customModelsActions = useCustomModelsActions();
  const notifications = useNotificationActions();
  const trackEvent = useTrackingEvent();
  const projectActions = useProjectActions();
  const { connectToChannel } = useConnectToChannel({
    page: PAGES.editor,
    origin: BRAND_VOICE_ASSET.customModel,
  });
  const handleError = useErrorHandler();
  const historyPushOrInfinityOpen = useHistoryPushOrInfinityOpen();
  const openConnectToChannelPopup = useConnectToChannelPopup();
  const openBlogCustomModelPopup = useBlogCustomModelPopup();
  const openAccountExtractionLoaderPopup = useAccountExtractionLoaderPopup();
  const emailConnectedAccounts = useEmailConnectedAccounts();

  return useCallback(
    async (onToneOfVoicePopupClose) => {
      let channel = getToolChannel(generationTool);

      trackEvent(events.DOCUMENT.clickedOpenCreateCustomModelPopup, {
        page: events.EVENT_PAGE.editor,
        generationTool,
        channel,
      });

      if (brandVoices.length > 1) {
        onToneOfVoicePopupClose?.();
        historyPushOrInfinityOpen(
          `${ROUTES.BRAND_VOICE}/${brandVoices[0].id}${ROUTES.CUSTOM_MODELS}`
        );
        return;
      }

      if (brandVoices.length === 1) {
        const brandVoiceId = brandVoices[0].id;
        const shouldProceed = onToneOfVoicePopupClose ? true : await openCustomModelPopup();

        if (shouldProceed) {
          try {
            const onConnectSuccess = async ({ customModelId, brandVoiceId }) => {
              const customModels = await customModelsActions.queryCustomModels([brandVoiceId]);
              const customModel = customModels.find((cm) => cm.id === customModelId);
              if (customModel?.processingError) {
                notifications.displayError('Failed to create custom model');
              } else if (customModel?.status === CUSTOM_MODEL_STATUS.active) {
                brandVoicesActions.storeBrandVoiceItemInCache({
                  itemsField: BRAND_VOICE_ASSET_FIELD.customModels,
                  item: customModel,
                  brandVoiceId,
                });
                projectActions.setSelectedCustomModelId(customModel.id);
                onSelect?.(customModelId);
                openCustomModelSuccessPopup({ customModel });
              }
            };
            onToneOfVoicePopupClose?.();

            if (isMetaChannel(channel)) {
              injectFacebookSDK();
            }

            if (channel === CHANNEL.emailChannel && !emailConnectedAccounts.length) {
              const emailChannel = await openConnectToChannelPopup({
                brandVoiceId,
                onConnectSuccess,
                displayResourcesTemplates: true,
                displayEmailIntegrations: true,
                title: 'Train a custom model',
                subtitle: 'Select the data you want train the model on',
                isTrainingModel: true,
              });
              if (!getIsEmailChannel(emailChannel)) {
                return;
              } else {
                channel = emailChannel;
              }
            } else if (isBlogUrlsChannel(channel)) {
              const result = await openBlogCustomModelPopup({ brandVoiceId, onConnectSuccess });

              if (result) {
                openAccountExtractionLoaderPopup({
                  customModelAccount: result,
                  isTrainingModel: true,
                  isAlreadyConnected: true,
                });
              }
              return;
            }

            await connectToChannel({
              channel,
              brandVoiceId,
              isTrainingModel: true,
              onConnectSuccess,
              hasEmailConnectedAccounts: emailConnectedAccounts.length > 0,
            });
            trackEvent(events.CUSTOM_MODEL.clickedModelTrained, {
              page: events.EVENT_PAGE.editor,
              generationTool,
              channel,
            });
          } catch (error) {
            handleError(error);
          }
        }
      }
    },
    [
      trackEvent,
      brandVoices,
      openCustomModelPopup,
      connectToChannel,
      historyPushOrInfinityOpen,
      brandVoicesActions,
      openCustomModelSuccessPopup,
      notifications,
      projectActions,
      generationTool,
      handleError,
      customModelsActions,
      openConnectToChannelPopup,
      onSelect,
      openBlogCustomModelPopup,
      openAccountExtractionLoaderPopup,
      emailConnectedAccounts,
    ]
  );
};
